import { action, computed, observable } from 'mobx';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { IMemberStore, UserRole } from '@stores/member';
import { AxiosResponse } from 'axios';
import HttpStatusCode from '@utils/httpStatusCodes';
import { MemberStore } from '../member';
import { hasExhaustedLimit } from '@components/directory/utils';
import { getApiErrorMessage } from '@components/common/util/util';

type ServerWorkspace = {
  id: string;
  name: string;
  hosted: boolean;
  enableCloudSources: boolean;
  enableWarehouseActions: boolean;
  cloudSourcesLimit: number;
  warehouseActionsLimit: number;
  transformationsLimit: number;
  enableTeams: boolean;
  enableGrafana: boolean;
  dataPlaneURL: string;
  ownerId: string;
  enableTrackingPlansUI: boolean;
  userRoles: Member[];
  invitedUsers: (IMemberStore & { createdAt: string })[];
  organizationId: string;
  orgSettings: {
    name: string;
    enforcedMfa: boolean;
    freetrial: boolean;
    pro: boolean;
    enterprise: boolean;
  } & Record<string, boolean>;
  piiPermissions: { isLocked: boolean; id: string };
  enablePermissions: boolean;
};

export interface IWorkspace {
  id: string;
  name: string;
  dataPlane: string;
  hosted: boolean;
  hasExhaustedCloudLimit: boolean;
  cloudSourcesLimit: number;
  hasExhaustedWHSourcesLimit: boolean;
  enableReports: boolean;
  warehouseActionsLimit: number;
  enableTeams: boolean;
  hasExhaustedTransformationsLimit: boolean;
  transformationsLimit: number;
  enableGrafana: boolean;
  ownerId: string;
  members: IMemberStore[];
  isSyncEnabled: boolean;
  isEventLimitReached: boolean;
  enableEventLimit: boolean;
  firstLoad: boolean;
  isTrulyHosted: boolean;
  isWorkspaceOwner: boolean;
  whActionsSyncBehaviours: string[];
  eventStreamLimit: number;
  enableTrackingPlansUI: boolean;
  enablePermissions: boolean;
  enabledSources: string[];
}
export interface IWorkspaceStore extends IWorkspace {
  rootStore: IRootStore;
  getWorkspace(): void;
  getSettings: () => Promise<void>;
  requestAdminToUpgrade: () => Promise<boolean>;
  updateName: (e: string) => Promise<boolean>;
  reset: () => void;
}

interface CustomerDetails {
  settings: {
    whActionsSyncBehaviours: string[];
  };
  enableReports: boolean;
}
interface Member {
  user: IMemberStore;
  role: UserRole;
}

export class WorkspaceStore implements IWorkspaceStore {
  @observable public id = '';

  @observable public name = '';

  @observable public hosted = false;

  @observable public enableTeams = false;

  @observable public enableGrafana = false;

  @observable public enableCloudSources = false;

  @observable public cloudSourcesLimit = 0;

  @observable public enableWarehouseActions = false;

  @observable public warehouseActionsLimit = 0;

  @observable public ownerId = '';

  @observable public members = [] as MemberStore[];

  @observable public rootStore: IRootStore;

  @observable public dataPlaneURL = '';

  @observable public isSyncEnabled = false;

  @observable public enableReports = false;

  @observable public isEventLimitReached = false;

  @observable public firstLoad = false;

  @observable public eventStreamLimit = 0;

  @observable public whActionsSyncBehaviours: string[] = [];

  @observable public transformationsLimit = 0;

  @observable public enableTrackingPlansUI = false;

  @observable public enablePermissions = false;

  @observable public enabledSources: string[] = [];

  // Add variables in reset method as well

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action.bound
  public changeCustomerDetails(customerDetails: CustomerDetails) {
    this.whActionsSyncBehaviours = customerDetails?.settings?.whActionsSyncBehaviours || [];
    this.enableReports = !!customerDetails?.enableReports;
  }

  @action.bound
  public async getWorkspace() {
    const { userStore, billingStore } = this.rootStore;
    const { token, selectedWorkspaceId, email } = userStore;
    if (!selectedWorkspaceId) {
      userStore.signOutUser();
      return;
    }
    await this.getCustomerDetails(token, email);
    await this.getSettings();
    this.firstLoad = true;
    if (this.isTrulyHosted) {
      this.getIsEventLimitReached();
    }
    billingStore.getTenantDetail();
  }

  private async getCustomerDetails(token: string, email: string) {
    await apiAuthCaller(token)
      .get(`/customers/${email}`)
      .then((res) => {
        this.changeCustomerDetails(res.data);
      })
      .catch(() => null);
  }

  getEnabledSources(settings: Record<string, boolean>) {
    const { sourceDefinitionsListStore } = this.rootStore;
    return sourceDefinitionsListStore.sourceDefinitions
      .filter(({ name }) => settings[`enable_${name.toLowerCase()}_source`])
      .map(({ name }) => name);
  }

  public async getSettings() {
    const {
      userStore: { token },
      permissionsStore,
      organizationStore,
    } = this.rootStore;
    try {
      const resp = await apiAuthCaller(token).get<{ workspace: ServerWorkspace }>('/settings');
      const workspaceData = resp.data.workspace;
      this.id = workspaceData.id;
      this.name = workspaceData.name;
      this.hosted = workspaceData.hosted;
      this.enableCloudSources = workspaceData.enableCloudSources;
      this.cloudSourcesLimit = workspaceData.cloudSourcesLimit;
      this.enableWarehouseActions = workspaceData.enableWarehouseActions;
      this.warehouseActionsLimit = workspaceData.warehouseActionsLimit;
      this.transformationsLimit = workspaceData.transformationsLimit;
      this.enableTeams = workspaceData.enableTeams;
      this.enablePermissions = workspaceData.enablePermissions;
      this.enableGrafana = workspaceData.enableGrafana;
      this.dataPlaneURL = workspaceData.dataPlaneURL;
      this.ownerId = workspaceData.ownerId;
      this.enableTrackingPlansUI = workspaceData.enableTrackingPlansUI;
      this.enabledSources = this.getEnabledSources(workspaceData.orgSettings);
      this.populateMembers(workspaceData);
      this.identifyAccountType();
      permissionsStore.setDataPermissions(workspaceData.piiPermissions);
      organizationStore.seed({ id: workspaceData.organizationId, ...workspaceData.orgSettings });
    } catch (err) {}
  }

  private populateMembers(workspaceData: {
    userRoles: Member[];
    invitedUsers: (IMemberStore & { createdAt: string })[];
  }) {
    this.members = workspaceData.userRoles.map(
      (userRole) =>
        new MemberStore(
          {
            id: userRole.user.id,
            name: userRole.user.name,
            email: userRole.user.email,
            status: undefined,
            role: userRole.role,
            invitedAt: '',
          },
          this.rootStore,
        ),
    );
    this.members = this.members.concat(
      workspaceData.invitedUsers.map(
        (user) =>
          new MemberStore(
            {
              id: user.id,
              name: user.name,
              email: user.email,
              status: user.status,
              role: user.role,
              invitedAt: user.createdAt,
            },
            this.rootStore,
          ),
      ),
    );
  }

  private identifyAccountType() {
    const { userStore, organizationStore } = this.rootStore;
    const userId = userStore.id;
    let accountType = organizationStore.isFreeTrial ? 'free_trial' : 'open_source';
    if (this.ownerId !== userId) {
      accountType = 'invited';
    }
    window.rudderanalytics.identify(
      {
        account_type: accountType,
        first_login: false,
      },
      {
        integrations: {
          Salesforce: true,
        },
      },
    );
  }

  @computed get isTrulyHosted() {
    return this.hosted && this.rootStore.organizationStore.isFreeTrial;
  }

  @computed get isWorkspaceOwner() {
    return !!this.ownerId && this.ownerId === this.rootStore.userStore.id;
  }

  @computed get enableEventLimit() {
    return this.isTrulyHosted && this.isEventLimitReached;
  }

  @computed get hasExhaustedCloudLimit() {
    return (
      !this.enableCloudSources ||
      hasExhaustedLimit({
        limit: this.cloudSourcesLimit,
        count: this.rootStore.sourcesListStore.cloudSourcesCount,
      })
    );
  }

  @computed get hasExhaustedWHSourcesLimit() {
    return (
      !this.enableWarehouseActions ||
      hasExhaustedLimit({
        limit: this.warehouseActionsLimit,
        count: this.rootStore.sourcesListStore.warehouseSourcesCount,
      })
    );
  }

  @action.bound
  private async getIsEventLimitReached() {
    const { userStore } = this.rootStore;
    const { token } = userStore;
    interface IResp {
      eventStreamLimit: number;
      eventStreamCount: number;
    }
    try {
      const resp: AxiosResponse<IResp> = await apiAuthCaller(token).get('/eventUsageStats');
      this.eventStreamLimit = resp.data.eventStreamLimit;
      this.isEventLimitReached = resp.data.eventStreamCount > resp.data.eventStreamLimit;
    } catch (err) {}
  }

  @action.bound
  public async requestAdminToUpgrade() {
    const { userStore } = this.rootStore;
    const { token } = userStore;
    try {
      const resp = await apiAuthCaller(token).post('/requestAdminToUpgrade');
      return resp.status === 200;
    } catch (error) {
      // Handle error
      return false;
    }
  }

  @action.bound
  public async updateName(name: string) {
    const BI_SUCCESS_MESSAGE = 'General workspace name updated successfully';
    const BI_FAILURE_MESSAGE = 'General workspace name update failure';
    const {
      userStore,
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    const { token, email } = userStore;
    try {
      const resp = await apiAuthCaller(token).post('/updateName', { name });
      if (resp.status === HttpStatusCode.Ok) {
        this.name = name;
        window.rudderanalytics.track(BI_SUCCESS_MESSAGE);
        return true;
      }
      return false;
    } catch (error) {
      showErrorMessage(getApiErrorMessage(error, 'Failed to update workspace name'));
      window.rudderanalytics.track(BI_FAILURE_MESSAGE, {
        name,
        email,
        error,
      });
      return false;
    }
  }

  @computed get dataPlane() {
    const { userStore, organizationStore } = this.rootStore;
    if (organizationStore.isOpenSource) {
      return '';
    }
    return this.dataPlaneURL || userStore.dataPlaneUrl;
  }

  @computed get hasExhaustedTransformationsLimit() {
    const { transformations } = this.rootStore.transformationsListStore;
    return transformations.length >= this.transformationsLimit;
  }

  reset() {
    this.id = '';
    this.name = '';
    this.hosted = false;
    this.enableTeams = false;
    this.enableGrafana = false;
    this.enableCloudSources = false;
    this.cloudSourcesLimit = 0;
    this.enableWarehouseActions = false;
    this.warehouseActionsLimit = 0;
    this.ownerId = '';
    this.members = [];
    this.dataPlaneURL = '';
    this.isSyncEnabled = false;
    this.enableReports = false;
    this.isEventLimitReached = false;
    this.whActionsSyncBehaviours = [];
    this.transformationsLimit = 0;
    this.enableTrackingPlansUI = false;
    this.enablePermissions = false;
    this.enabledSources = [];
  }
}
